import { useCallback, useMemo, useState } from "react"
import { bulkCreateShippingCoverages } from "../../../../services"

export const DRAFT_STATUS = {
  EDITING: "editing",
  VALIDATING: "validating",
  VALIDATED_OK: "validated_ok",
  VALIDATED_WITH_ERRORS: "validated_with_errors",
  COMMITTING: "committing",
  COMMITTED: "committed",
  ERROR: "error",
}

export function useCreateCoveragesDraft({ shippingMethodId }) {
  const [coverages, setCoveragesState] = useState([])
  const [xdockBindings, setXdockBindings] = useState([])
  const [shippingCosts, setShippingCosts] = useState([])
  const [status, setStatus] = useState(DRAFT_STATUS.EDITING)
  const [response, setResponse] = useState(null) // last server response

  // Any change to the batch invalidates the previous validation: row errors are
  // keyed by index into the old coverages array, so editing/reparsing/removing
  // rows would otherwise display stale errors on rows that no longer match.
  const setCoverages = useCallback((next) => {
    setCoveragesState((prev) => (typeof next === "function" ? next(prev) : next))
    setStatus(DRAFT_STATUS.EDITING)
    setResponse(null)
  }, [])

  const appendXdockBinding = useCallback((xdockId, zoneNames) => {
    setXdockBindings((prev) => {
      // merge into existing binding for the same xdockId
      const existing = prev.find((b) => b.xdock_id === xdockId)
      if (existing) {
        const merged = Array.from(new Set([...existing.zone_names, ...zoneNames]))
        return prev.map((b) => (b.xdock_id === xdockId ? { ...b, zone_names: merged } : b))
      }
      return [...prev, { xdock_id: xdockId, zone_names: [...zoneNames] }]
    })
    setStatus(DRAFT_STATUS.EDITING)
  }, [])

  const removeXdockBinding = useCallback((xdockId, zoneName) => {
    setXdockBindings((prev) =>
      prev
        .map((b) =>
          b.xdock_id === xdockId
            ? { ...b, zone_names: b.zone_names.filter((z) => z !== zoneName) }
            : b
        )
        .filter((b) => b.zone_names.length > 0)
    )
    setStatus(DRAFT_STATUS.EDITING)
  }, [])

  const appendShippingCost = useCallback((cost) => {
    setShippingCosts((prev) => {
      const existingIdx = prev.findIndex((c) => c.tarif_zone === cost.tarif_zone)
      if (existingIdx >= 0) {
        const next = [...prev]
        next[existingIdx] = { ...next[existingIdx], ...cost }
        return next
      }
      return [...prev, cost]
    })
    setStatus(DRAFT_STATUS.EDITING)
  }, [])

  const removeShippingCost = useCallback((tarifZone) => {
    setShippingCosts((prev) => prev.filter((c) => c.tarif_zone !== tarifZone))
    setStatus(DRAFT_STATUS.EDITING)
  }, [])

  const buildPayload = useCallback(
    (dryRun) => {
      const payload = {
        shipping_method_id: parseInt(shippingMethodId, 10),
        dry_run: dryRun,
        coverages,
      }
      const prereqs = {}
      if (xdockBindings.length > 0) prereqs.xdock_zone_bindings = xdockBindings
      if (shippingCosts.length > 0) prereqs.shipping_costs = shippingCosts
      if (Object.keys(prereqs).length > 0) payload.prerequisites = prereqs
      return payload
    },
    [shippingMethodId, coverages, xdockBindings, shippingCosts]
  )

  const validate = useCallback(async () => {
    setStatus(DRAFT_STATUS.VALIDATING)
    try {
      const data = await bulkCreateShippingCoverages(buildPayload(true))
      setResponse(data)
      if (data.error_code === "BULK_CREATE_VALIDATION_FAILED") {
        setStatus(DRAFT_STATUS.VALIDATED_WITH_ERRORS)
      } else if (data.dry_run === true) {
        setStatus(DRAFT_STATUS.VALIDATED_OK)
      } else {
        setStatus(DRAFT_STATUS.ERROR)
      }
      return data
    } catch (err) {
      setResponse({ error: err.message, error_code: "NETWORK_ERROR" })
      setStatus(DRAFT_STATUS.ERROR)
      return null
    }
  }, [buildPayload])

  const commit = useCallback(async () => {
    setStatus(DRAFT_STATUS.COMMITTING)
    try {
      const data = await bulkCreateShippingCoverages(buildPayload(false))
      setResponse(data)
      if (data.job_id) {
        setStatus(DRAFT_STATUS.COMMITTED)
      } else {
        // Server changed its mind between dry-run and real run
        setStatus(DRAFT_STATUS.VALIDATED_WITH_ERRORS)
      }
      return data
    } catch (err) {
      setResponse({ error: err.message, error_code: "NETWORK_ERROR" })
      setStatus(DRAFT_STATUS.ERROR)
      return null
    }
  }, [buildPayload])

  const reset = useCallback(() => {
    setCoveragesState([])
    setXdockBindings([])
    setShippingCosts([])
    setStatus(DRAFT_STATUS.EDITING)
    setResponse(null)
  }, [])

  // Convenience: index errors by row index for fast lookup in the table
  const errorsByIndex = useMemo(() => {
    if (!response || !response.rows) return {}
    return Object.fromEntries(response.rows.map((r) => [r.index, r.errors]))
  }, [response])

  return {
    coverages,
    setCoverages,
    xdockBindings,
    shippingCosts,
    status,
    response,
    errorsByIndex,
    appendXdockBinding,
    removeXdockBinding,
    appendShippingCost,
    removeShippingCost,
    validate,
    commit,
    reset,
    canValidate: coverages.length > 0,
    canCommit:
      status === DRAFT_STATUS.VALIDATED_OK ||
      (status === DRAFT_STATUS.VALIDATING && false), // require an OK dry-run first
  }
}
